/**
 * sql-button-settings — ustawienia klocka „Przycisk SQL” (`sql_button`) w układzie formularza.
 *
 * Kreator zapisuje w `element.settings` dwa pola: `label` (napis na przycisku) i `procedureId`
 * (procedura kartoteki „Na przycisk”). Strona rekordu czyta je defensywnie — układ z bazy to
 * nieufny JSON (stary / ręcznie edytowany / zaimportowany).
 */

/** Typ elementu układu — ten sam klucz, pod którym kreator rejestruje klocek. */
export const SQL_BUTTON_ELEMENT_TYPE = 'sql_button';

/**
 * Najdłuższy napis na przycisku (znaków). Kreator ogranicza pole tym samym limitem, a strona rekordu
 * przycina nim napis z układu — układ z bazy to nieufny JSON (stary / zaimportowany), więc zbyt długi
 * napis nie rozepchnie formularza ani komunikatów o zadaniu.
 */
export const SQL_BUTTON_LABEL_MAX_LENGTH = 60;

export interface SqlButtonSettings {
  label: string;
  procedureId: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Napis bez odstępów na brzegach, przycięty do limitu (liczonego jak `maxLength` pola — w jednostkach UTF-16). */
function normalizeLabel(raw: string): string {
  const cut = raw.trim().slice(0, SQL_BUTTON_LABEL_MAX_LENGTH);
  // Cięcie w środku pary zastępczej (np. emoji) zostawiłoby w napisie uszkodzony znak.
  const last = cut.charCodeAt(cut.length - 1);
  const whole = last >= 0xd800 && last <= 0xdbff ? cut.slice(0, -1) : cut;
  return whole.trimEnd();
}

/**
 * Ustawienia klocka albo `null` — klocek bez procedury albo z pustym napisem nie renderuje
 * nic na stronie rekordu (kreator oznacza takie klocki jako „Niekompletny"). Napis dłuższy niż
 * `SQL_BUTTON_LABEL_MAX_LENGTH` jest przycinany.
 */
export function parseSqlButtonSettings(settings: unknown): SqlButtonSettings | null {
  if (!isRecord(settings)) return null;
  const label = typeof settings.label === 'string' ? normalizeLabel(settings.label) : '';
  const procedureId = typeof settings.procedureId === 'string' ? settings.procedureId.trim() : '';
  if (label === '' || procedureId === '') return null;
  return { label, procedureId };
}

/**
 * Napisy przycisków układu wg id procedury — do komunikatów o zadaniu („<napis>: …”, pasek blokady):
 * zadanie zna tylko nazwę procedury z katalogu, a użytkownik zna przycisk po napisie. Przeszukuje
 * cały układ rekurencyjnie (zakładki, sekcje, akordeony…), bez znajomości jego struktury; przy dwóch
 * przyciskach tej samej procedury wygrywa pierwszy napotkany.
 */
export function collectSqlButtonLabels(layout: unknown): ReadonlyMap<string, string> {
  const labels = new Map<string, string>();
  const visit = (node: unknown): void => {
    if (Array.isArray(node)) {
      for (const child of node) visit(child);
      return;
    }
    if (!isRecord(node)) return;
    if (node.type === SQL_BUTTON_ELEMENT_TYPE) {
      const parsed = parseSqlButtonSettings(node.settings);
      if (parsed !== null && !labels.has(parsed.procedureId)) {
        labels.set(parsed.procedureId, parsed.label);
      }
    }
    for (const value of Object.values(node)) visit(value);
  };
  visit(layout);
  return labels;
}
